// router/navigation.ts - Navigation Utilities

import { getRoute, extractRouteParams } from "./routes";
import {
	getElementByIdSafe,
	clearPageContainers,
	adjustBodyPaddingForFooter,
} from "../utils/domHelpers";
import { renderErrorMessage } from "../components/common/toastMessage";
import { showSurveyCtaIfEligible } from "../components/common/surveyCta";
import { isExcludedPath } from "./leaderboardRules";
import { createAndRenderNav } from "../components/core/navigation";
import { updateHeaderPaddingAll } from "../components/core/header";
import { updateNavVisibility } from "../components/core/navigation";
import { checkAndShowPendingPrizeModal, checkAndShowRegisterPrompt } from "../utils/innerCirclePrizeModal";
import { CatalogueUnavailableError } from "../services/competitionCatalogue";
import { renderCatalogueUnavailable } from "../components/common/catalogueUnavailable";

/**
 * Navigate to a new path with client-side routing
 * @param path - The path to navigate to
 * @param options - Navigation options
 */
export async function navigateTo(
	path: string,
	options: { replace?: boolean; skipRender?: boolean } = {}
): Promise<void> {
	const { replace = false, skipRender = false } = options;

	// Update browser history
	if (replace) {
		window.history.replaceState({}, "", path);
	} else {
		window.history.pushState({}, "", path);
	}

	// Render the new route if not skipping
	if (!skipRender) {
		await handleRouteChange(path);
	}
}

/**
 * Handle route changes and render the appropriate page
 * @param path - The path to render (can include hash and query params)
 */
export async function handleRouteChange(path?: string): Promise<void> {
	// Parse the path to separate pathname from hash and query
	let pathname: string;
	let hash: string = "";
	const currentPath =
		path ||
		window.location.pathname + window.location.search + window.location.hash;
	const url = new URL(currentPath, window.location.origin);
	pathname = url.pathname;
	hash = url.hash;

	// Update nav visibility based on the new path
	updateNavVisibility();

	const contentContainer = getElementByIdSafe("content");

	if (!contentContainer) {
		console.error("Main content container (#content) not found!");
		return;
	}

	// Clear all page containers before rendering to prevent content bleeding
	clearPageContainers();

	try {
		// Get the route definition using just the pathname
		const route = getRoute(pathname);

		if (route) {
			// Set page title
			if (typeof route.title === "function") {
				const params = extractRouteParams(pathname, route);
				document.title = route.title(params);
			} else {
				document.title = route.title;
			}

			// Clear loading state and render the page
			await route.handler();

			// Handle scrolling based on hash
			if (hash) {
				// Wait a bit for content to render, then scroll to hash
				setTimeout(() => {
					try {
						const element = document.querySelector(hash);
						if (element) {
							element.scrollIntoView({ behavior: "smooth" });
						}
					} catch (e) {
						console.warn(`Invalid selector for hash: ${hash}`, e);
					}
				}, 100);
			} else {
				// Scroll to top if no hash
				window.scrollTo(0, 0);
				// Delayed scroll for iOS: layout/viewport can settle after first paint, causing restored or wrong scroll
				setTimeout(() => window.scrollTo(0, 0), 150);
			}

			// Adjust footer padding after content is rendered
			adjustBodyPaddingForFooter();

			// Trigger survey CTA after page render
			showSurveyCtaIfEligible().catch((error) => {
				console.warn("Error showing survey CTA:", error);
			});

			// Check and show pending inner circle prize modal if applicable
			// This will only show once per user (tracked via localStorage)
			// and only on non-entry pages
			checkAndShowPendingPrizeModal(pathname).catch((error) => {
				console.warn("Error checking pending prize modal:", error);
			});

			// Check and show register prompt for temp users on other pages (non-entry, non-home)
			// Only shows if temp user hasn't won prize and hasn't submitted email
			checkAndShowRegisterPrompt(pathname).catch((error) => {
				console.warn("Error checking register prompt:", error);
			});

			// Enforce leaderboard visibility rules on route change
			try {
				const p = pathname;
				const shouldHide = isExcludedPath(p);
				if (shouldHide) {
					const m = document.getElementById("mobile-leaderboard-row");
					const d = document.getElementById("desktop-leaderboard-row");
					m?.remove();
					d?.remove();
					// After removal, re-apply header padding
					updateHeaderPaddingAll();
				} else {
					// If allowed, ensure rows exist (and user didn't close them)
					const isClosed = sessionStorage.getItem("leaderboardClosed") === "true";
					const m = document.getElementById("mobile-leaderboard-row");
					const d = document.getElementById("desktop-leaderboard-row");
					if (!isClosed && (!m || !d)) {
						// Re-render nav to recreate leaderboard rows according to current state
						createAndRenderNav();
						// Recalculate header padding after nav rows render
						setTimeout(() => updateHeaderPaddingAll(), 0);
					}
				}
			} catch (_) {
				/* ignore */
			}
		} else {
			// Route not found in client-side router
			console.warn(
				`No client-side handler for path: ${pathname}. This should not happen if link interceptor is working correctly.`
			);
			// This should not happen if link interceptor is working correctly
			// Fall back to full page reload
			window.location.href = pathname + (hash || "");
		}
	} catch (pageError: any) {
		console.error(`Error rendering page for path ${pathname}:`, pageError);
		if (pageError instanceof CatalogueUnavailableError) {
			contentContainer.innerHTML = "";
			renderCatalogueUnavailable(contentContainer, () => handleRouteChange(currentPath));
			return;
		}
		renderErrorMessage(
			"content",
			`Error loading page: ${pageError.message || "Unknown error"}`
		);
	}
}

/**
 * Check if a URL is external
 * @param url - The URL to check
 * @returns true if the URL is external
 */
export function isExternalUrl(url: string): boolean {
	try {
		const urlObj = new URL(url, window.location.origin);
		return urlObj.origin !== window.location.origin;
	} catch {
		// If URL parsing fails, treat as internal relative URL
		return false;
	}
}
